
import { useEffect, createContext } from "react";
import { TemplateSelector } from "./templates/TemplateSelector";
import { ProjectListHeader } from "./project-list/ProjectListHeader";
import { ProjectTable } from "./project-list/ProjectTable";
import { TemplateDialog } from "./dialogs/TemplateDialog";
import { ProjectDialogs } from "./dialogs/ProjectDialogs";
import { CustomProjectDialog } from "./dialogs/CustomProjectDialog";
import { templates } from "./data/templateData";
import { useTemplates } from "./hooks/useTemplates";
import { useProjects } from "./hooks/useProjects";
import { useAssignedProjects } from "./hooks/useAssignedProjects";
import { useUserRole } from "./hooks/useUserRole";
import { useTemplateCarouselAccess } from "./hooks/useTemplateCarouselAccess";
import { useEditProjectAccess } from "./hooks/useEditProjectAccess";
import { useDeleteProjectAccess } from "./hooks/useDeleteProjectAccess";
import { 
  AlertDialog,
  AlertDialogContent,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogAction
} from "@/components/ui/alert-dialog";
import { Project } from "./types";

// Create ProjectContext
export const ProjectContext = createContext<{
  selectedProject: Project | null;
  projects: Project[];
  selectedProjectId: string | null;
  loading: boolean;
}>({
  selectedProject: null,
  projects: [],
  selectedProjectId: null,
  loading: true
});

interface ProjectStructureTabProps {
  onProjectsChange: (projects: Project[]) => void;
  onSelectedProjectChange: (id: string | null) => void;
  selectedProjectId: string | null;
  user: any;
}

const ProjectStructureTab = ({ 
  onProjectsChange, 
  onSelectedProjectChange,
  selectedProjectId,
  user
}: ProjectStructureTabProps) => {
  // Get user role
  const { role } = useUserRole(user);
  
  // Check if user is Team member or Team Lead (used by ProjectListHeader for action buttons only)
  const isRestrictedUser = role === "Team member" || role === "Team Lead";

  // Per-company permission for the Template Carousel section
  const { canViewTemplateCarousel } = useTemplateCarouselAccess(user);
  const hideTemplateSection = !canViewTemplateCarousel;

  // Per-company permission for Edit Project icon
  const { canEditProject } = useEditProjectAccess(user);

  // Per-company permission for Delete Project icon
  const { canDeleteProject } = useDeleteProjectAccess(user);
  
  // Initialize hooks
  const templateHook = useTemplates();
  const projectHook = useProjects(templates);
  const assignedProjectsHook = useAssignedProjects();
  
  // Combine owned and assigned projects, avoiding duplicates
  const allProjects = [
    ...projectHook.projects,
    ...assignedProjectsHook.assignedProjects.filter(
      assignedProject => !projectHook.projects.some(
        ownedProject => ownedProject.id === assignedProject.id
      )
    )
  ];
  
  // Combined loading state
  const combinedLoading = projectHook.loading || assignedProjectsHook.loading;
  
  // Sync combined projects with parent component
  useEffect(() => {
    if (allProjects.length > 0) {
      onProjectsChange(allProjects);
    }
  }, [allProjects, onProjectsChange]);
  
  // Handle selected project sync between parent and this component
  useEffect(() => {
    // If parent has a selection, use it in the local state
    if (selectedProjectId !== null && selectedProjectId !== projectHook.selectedProjectId) {
      projectHook.setSelectedProjectId(selectedProjectId);
    } 
    // If local state has changed, update the parent
    else if (projectHook.selectedProjectId !== null && projectHook.selectedProjectId !== selectedProjectId) {
      onSelectedProjectChange(projectHook.selectedProjectId);
    }
  }, [selectedProjectId, projectHook.selectedProjectId, onSelectedProjectChange]);

  const handleApplyTemplate = async (e: React.FormEvent) => {
    const success = await projectHook.handleApplyTemplate(
      e,
      templateHook.selectedTemplate,
      templateHook.selectedTemplateTasks,
      templateHook.templateProjectName,
      templateHook.templateStartDate,
      templateHook.templateEndDate,
      templateHook.templateProjectDescription,
      templateHook.templateProjectLead,
      templateHook.excludeWeekends
    );
    
    if (success) {
      templateHook.setIsTemplateDialogOpen(false);
    }
  };

  const handleCreateCustomProject = async (projectData: {
    name: string;
    description: string;
    projectLead: string;
    startDate: string;
    endDate: string;
    duration: number;
    tasks: any[];
    excludeWeekends: boolean;
  }) => {
    const success = await projectHook.handleApplyTemplate(
      { preventDefault: () => {} } as React.FormEvent,
      "Custom project",
      projectData.tasks,
      projectData.name,
      projectData.startDate,
      projectData.endDate,
      projectData.description,
      projectData.projectLead,
      projectData.excludeWeekends
    );
    
    if (success) {
      templateHook.setIsCustomProjectDialogOpen(false);
    }
  };

  // Map projects to the expected format for duplicate name checking
  const existingProjectsForValidation = allProjects.map(project => ({
    project_name: project.name
  }));

  return (
    <ProjectContext.Provider
      value={{
        selectedProject: projectHook.selectedProject,
        projects: allProjects,
        selectedProjectId: selectedProjectId,
        loading: combinedLoading
      }}
    >
      <div className="space-y-6">
        {/* Template Selection Area - Only show for non-restricted users */}
        {!hideTemplateSection && (
          <TemplateSelector 
            templates={templateHook.templates}
            loading={templateHook.templatesLoading}
            onImportClick={() => templateHook.setIsImportAlertOpen(true)}
            onUseTemplate={templateHook.handleUseTemplate}
          />
        )}
        
        {/* Projects List Area */}
        <div className={hideTemplateSection ? "mt-0" : "mt-8"}>
          <ProjectListHeader
            activeTemplateName={projectHook.activeTemplateForProjects}
            startDate={projectHook.newProject.startDate}
            endDate={projectHook.newProject.endDate}
            duration={projectHook.newProject.duration}
            onNewProjectClick={() => projectHook.setIsNewProjectDialogOpen(true)}
            onCustomProjectClick={() => templateHook.setIsCustomProjectDialogOpen(true)}
            isRestrictedUser={isRestrictedUser}
          />
          
          {combinedLoading ? (
            <div className="flex justify-center py-8">
              <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : (
            <ProjectTable
              projects={allProjects}
              selectedProjectId={selectedProjectId}
              onLoadProject={(project) => {
                projectHook.handleLoadProject(project);
                onSelectedProjectChange(project.id);
              }}
              onEditProject={projectHook.handleEditProject}
              onDeleteProject={projectHook.handleDeleteProject}
              canEditProject={canEditProject}
              canDeleteProject={canDeleteProject}
            />
          )}
        </div>

        {/* Template Dialog - Only for non-restricted users */}
        {!hideTemplateSection && (
          <TemplateDialog
            isOpen={templateHook.isTemplateDialogOpen}
            onOpenChange={templateHook.setIsTemplateDialogOpen}
            selectedTemplate={templateHook.selectedTemplate}
            templateTasks={templateHook.selectedTemplateTasks}
            onTaskDurationChange={templateHook.handleTaskDurationChange}
            startDate={templateHook.templateStartDate}
            endDate={templateHook.templateEndDate}
            projectName={templateHook.templateProjectName}
            projectDescription={templateHook.templateProjectDescription}
            projectLead={templateHook.templateProjectLead}
            onApplyTemplate={handleApplyTemplate}
            onStartDateChange={templateHook.handleTemplateStartDateChange}
            onProjectNameChange={templateHook.setTemplateProjectName}
            onProjectDescriptionChange={templateHook.setTemplateProjectDescription}
            onProjectLeadChange={templateHook.setTemplateProjectLead}
            excludeWeekends={templateHook.excludeWeekends}
            onExcludeWeekendsChange={templateHook.handleExcludeWeekendsChange}
            existingProjects={existingProjectsForValidation}
          />
        )}

        {/* Custom Project Dialog - Only for non-restricted users */}
        {!hideTemplateSection && (
          <CustomProjectDialog
            isOpen={templateHook.isCustomProjectDialogOpen}
            onOpenChange={templateHook.setIsCustomProjectDialogOpen}
            onCreateProject={handleCreateCustomProject}
            existingProjects={existingProjectsForValidation}
          />
        )}

        {/* Simple Import Alert Dialog - Only for non-restricted users */}
        {!hideTemplateSection && (
          <AlertDialog 
            open={templateHook.isImportAlertOpen} 
            onOpenChange={templateHook.setIsImportAlertOpen}
          >
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Import Template</AlertDialogTitle>
                <AlertDialogDescription>
                  Check with your administrator to load or change project templates.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogAction onClick={() => templateHook.setIsImportAlertOpen(false)}>OK</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}

        {/* Project Dialogs (New, Edit, Delete) */}
        <ProjectDialogs
          isNewProjectDialogOpen={projectHook.isNewProjectDialogOpen}
          setIsNewProjectDialogOpen={projectHook.setIsNewProjectDialogOpen}
          isEditProjectDialogOpen={projectHook.isEditProjectDialogOpen}
          setIsEditProjectDialogOpen={projectHook.setIsEditProjectDialogOpen}
          isDeleteProjectDialogOpen={projectHook.isDeleteProjectDialogOpen}
          setIsDeleteProjectDialogOpen={projectHook.setIsDeleteProjectDialogOpen}
          isImportAlertOpen={false}
          setIsImportAlertOpen={() => {}}
          isImportDialogOpen={projectHook.isImportDialogOpen}
          setIsImportDialogOpen={projectHook.setIsImportDialogOpen}
          selectedProject={projectHook.selectedProject}
          newProject={projectHook.newProject}
          setNewProject={projectHook.setNewProject}
          handleCreateProject={projectHook.handleCreateProject}
          confirmEditProject={projectHook.confirmEditProject}
          confirmDeleteProject={projectHook.confirmDeleteProject}
          handleImportTemplate={() => {}}
          templates={templates}
          handleTaskDurationChange={projectHook.handleTaskDurationChange}
          excludeWeekends={projectHook.excludeWeekends}
          setExcludeWeekends={projectHook.setExcludeWeekends}
        />
      </div>
    </ProjectContext.Provider>
  );
};

export default ProjectStructureTab;
